import image from '@assets/images/file-icon/image.svg';
import bfile from '@assets/images/file-icon/file.svg';
import excel from '@assets/images/file-icon/excel.svg';
import word from '@assets/images/file-icon/word.svg';
import ppt from '@assets/images/file-icon/powerpoint.svg';
import pdf from '@assets/images/file-icon/pdf.svg';
import onenote from '@assets/images/file-icon/one note.svg';

import wordV2 from '@assets/images/file-icon/Word_v2.svg';
import excelV2 from '@assets/images/file-icon/excel_v2.svg';
import othersV2 from '@assets/images/file-icon/others_v2.svg';
import pdfV2 from '@assets/images/file-icon/pdf_v2.svg';
import pptV2 from '@assets/images/file-icon/powerpoint_v2.svg';
import imageV2 from '@assets/images/file-icon/image_v2.svg';

import imageThumbnail from '@assets/images/file-icon/image_thumbnail.svg';
import excelThumbnail from '@assets/images/file-icon/Excel_thumbnail.svg';
import wordThumbnail from '@assets/images/file-icon/Word_thumbnail.svg';
import othersThumbnail from '@assets/images/file-icon/others_thumbnail.svg';
import pdfThumbnail from '@assets/images/file-icon/pdf_thumbnail.svg';
import pptThumbnail from '@assets/images/file-icon/Powerpoint_thumbnail.svg';

import filerror from '@assets/images/file-icon/file error.svg';
import { guestAxiosEnv } from '@helpers/Axios';
import { BatchStatus } from '@interfaces/BatchStatus';
import type ProfileContentVO from '@interfaces/contents/ProfileContentVO';
import { getAnonymousViewerId } from '@utils/profile/ProfileAccessManagementUtils';

type SvgUrl = string;

type IconTypeEntry = {
  icon: string;
  svg: SvgUrl;
  v2_img?: SvgUrl;
  v2_large?: SvgUrl;
};

/** Minimal file shape for icon resolution (attachments, uploads, etc.) */
export type AttachFileIconInput = {
  fileType?: string;
  type?: string;
  name?: string;
  fileName?: string;
};

/** Minimal file shape for guest download URL construction */
export type FileDownloadInput = {
  fileType?: string;
  fileName?: string;
  name?: string;
  downloadAuthKey: string;
  token?: string;
};

/** File VO used when requesting watermarked preview metadata */
export type WatermarkPopulateFile = {
  id?: string;
  fileId?: string;
  fileName?: string;
  name?: string;
  fileType?: string;
  downloadAuthKey?: string;
  previewStatus?: BatchStatus;
};

/** Fields supplied to `viewSourceCallBack` after watermark batch completes */
export type WatermarkSourceUpdate = {
  fileId: string;
  downloadAuthKey: string;
  name: string;
};

export type PopulateWatermarkDownloadPayload = {
  docIds?: string[];
  attachmentIds?: string[];
  questionnaireIds?: string[];
  viewSourceCallBack?: (newFileVO: WatermarkSourceUpdate) => void;
  contentVO: ProfileContentVO | null | WatermarkPopulateFile | undefined;
};

const iconTypeMap: Record<string, IconTypeEntry> = {
  image: { icon: 'rfpio-font-icon icon-image', svg: image, v2_img: imageV2, v2_large: imageThumbnail },

  pdf: { icon: 'rfpio-font-icon icon-pdf', svg: pdf, v2_img: pdfV2, v2_large: pdfThumbnail },
  excel: { icon: 'rfpio-font-icon icon-excel', svg: excel, v2_img: excelV2, v2_large: excelThumbnail },
  word: { icon: 'rfpio-font-icon icon-word', svg: word, v2_img: wordV2, v2_large: wordThumbnail },
  ppt: { icon: 'rfpio-font-icon icon-power-point', svg: ppt, v2_img: pptV2, v2_large: pptThumbnail },
  onenote: { icon: 'rfpio-font-icon icon-import-file-view-fill', svg: onenote, v2_img: onenote },
  infected: { icon: 'rfpio-font-icon icon-warning', svg: filerror, v2_img: filerror },
  uploading: { icon: 'rfpio-font-icon icon-upload-cloud-2-line', svg: '', v2_img: '' },
  binary: { icon: 'rfpio-font-icon icon-import-file-view-fill', svg: bfile, v2_img: othersV2, v2_large: othersThumbnail }
};

const getFileType = (fileType: string) => {
  const fileTypeInLowerCase: string = fileType ? fileType.toLowerCase() : '';
  switch (fileTypeInLowerCase) {
    case 'image/png':
    case 'image/jpeg':
    case 'image':
      return 'image';

    case 'application/pdf':
    case 'pdf':
      return 'pdf';

    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel.sheet.macroenabled.12':
    case 'application/vnd.ms-excel':
    case 'xls':
    case 'xlsx':
    case 'xlsm':
    case 'xlsb':
    case 'excel_x':
      return 'excel';

    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/vnd.ms-word.document.macroenabled.12':
    case 'application/msword':
    case 'doc':
    case 'docx':
    case 'docm':
    case 'word_x':
      return 'word';

    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    case 'application/vnd.ms-powerpoint':
    case 'pptx':
    case 'ppsx':
    case 'power_point_x':
      return 'ppt';

    case 'one':
      return 'onenote';

    case 'Infected':
      return 'infected';

    case 'Uploading':
      return 'uploading';

    default:
      return 'binary';
  }
};

export const getAttachFileIcon = (file: AttachFileIconInput | null | undefined, iconType?: string) => {
  if (!file) {
    return '';
  }
  let fileType = '';
  if (file && (file.fileType || file.type)) {
    fileType = file.fileType || file.type || '';
  }
  if (!fileType) {
    if (file.name) {
      fileType = file.name.substring(file.name.lastIndexOf('.') + 1);
    } else if (file.fileName) {
      fileType = file.fileName.substring(file.fileName.lastIndexOf('.') + 1);
    }
  }
  const type = fileType?.split('/');
  if (type && type[0] === 'image') {
    if (iconType === 'v2_img') {
      return imageV2;
    }
    if (iconType === 'v2_large') {
      return imageThumbnail;
    }
    return image;
  }
  if (!iconType) {
    iconType = 'icon';
  }
  const entry = iconTypeMap[getFileType(fileType)];
  if (iconType === 'v2_img') {
    return entry.v2_img;
  }
  if (iconType === 'v2_large') {
    return entry.v2_large;
  }
  return entry.svg;
};

export const unSupportedFileTypes: string[] = [
  'application/gzip',
  'application/x-rar-compressed',
  'application/zip',
  'application/x-7z-compressed',
  'image/webp',
  'video/mp4',
  'video/mpeg',
  'video/x-mpeg',
  'video/webm',
  'application/x-troff-msvideo',
  'video/avi',
  'video/msvideo',
  'video/x-msvideo',
  'application/mp4',
  'video/x-ms-wmv',
  'video/x-ms-asf',
  'audio/mpeg',
  'audio/mpeg3',
  'audio/x-mpeg-3',
  'audio/wav',
  'audio/vnd.wave',
  'audio/x-wav',
  'audio/vorbis',
  'audio/webm',
  'audio/mp4'
];

const imageTypeList = ['image/bmp', 'image/x-ms-bmp', 'image/jpeg', 'image/png', 'image/gif', 'image/tiff', 'image/x-emf', 'image/emf', 'image/wmf', 'application/x-emf', 'application/x-msmetafile'];

export enum FILE_LOCATION {
  LOCAL = 'LOCAL',
  DOCUMENT_LIBRARY = 'DOCUMENT_LIBRARY',
  PROJECT_DOCS = 'PROJECT_DOCS',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  DROPBOX = 'DROPBOX',
  ONEDRIVE = 'ONEDRIVE',
  BOX = 'BOX',
  HIGHSPOT = 'HIGHSPOT',
  SEISMIC = 'SEISMIC',
  SIGNED_DOCUMENT = 'SIGNED_DOCUMENT_LIBRARY'
}

export const ACCEPTABLE_FILETYPES = ['.txt', '.pdf', '.doc', '.docx', '.xlsx', '.xls', '.xlsm', '.jpg', '.jpeg', '.png', '.svg', '.pptx', '.ppt', '.webp'];

export const checkIfFileIsImage = function checkIfFileIsImage(type: string) {
  return imageTypeList.includes(type);
};

export const checkIfFileIsExcel = (file: { fileType?: string; fileName?: string; name?: string }) => {
  const fileType = (file?.fileType || '').toLowerCase();
  if (getFileType(fileType) === 'excel') {
    return true;
  }
  const fileName = (file?.fileName || file?.name || '').toLowerCase();
  const extension = fileName.split('.').pop() || '';
  return getFileType(extension) === 'excel';
};

export const trimFileExtension = (fileName: string) => {
  const name: string = fileName;
  const idx = name.lastIndexOf('.');
  if (idx > -1) {
    return name.substring(0, name.lastIndexOf('.'));
  }
  return name;
};

export const getValidFileName = (fileName: string) => {
  return fileName.replace(/[?!@$^&*:><"'|;#%[\]/,]/g, '_');
};

export const getPdfUrl = () => {
  return `${window.location.origin}/profile-guest/pdfjs/viewer.html?file=`;
};

export const getFileDownloadURL = (file: FileDownloadInput | null | undefined, context: { urlPrefix: string; guestKey?: string }, isPreview?: boolean) => {
  if (!file) {
    return '';
  }
  const isImage: boolean = !!(file.fileType && imageTypeList?.includes(file.fileType));
  const fileName = getValidFileName(file.fileName || file.name || '');

  let url = `${window.constants.baseUrl}guest_v3/${context.urlPrefix}/download/${context.guestKey ? `${context.guestKey}/` : ''}${file.downloadAuthKey}/${fileName}`;
  if (isPreview) {
    const anonymousViewerId = getAnonymousViewerId();
    url += isImage
      ? `?podName=${guestAxiosEnv.podName}&isPreview=true${anonymousViewerId ? `&anonymousViewId=${anonymousViewerId}` : ''}`
      : `%3FpodName%3D${guestAxiosEnv.podName}%26isPreview%3Dtrue${anonymousViewerId ? `%26anonymousViewId%3D${anonymousViewerId}` : ''}`;
  }
  if (file.token) {
    if (isImage) {
      url += `&token=${file.token}`;
    } else {
      url += `%26token%3D${file.token}`;
    }
  }
  return url;
};

export const populateWatermarkFields = (
  fileVO: WatermarkPopulateFile | null | undefined,
  downloadProfileContent: (payload: PopulateWatermarkDownloadPayload) => void,
  sourceVO: ProfileContentVO | null | undefined,
  isDocument: boolean,
  setDocLoaded: (docLoaded: boolean) => void,
  setUpdatedData: (updated: WatermarkPopulateFile) => void
) => {
  setDocLoaded(false);

  if (!fileVO) {
    return {};
  }

  const key = isDocument ? 'docIds' : 'attachmentIds';
  const id = isDocument ? fileVO.id : fileVO.fileId;
  fileVO.name = fileVO.fileName || fileVO.name;

  const updatedFileVO = { ...fileVO };

  const viewSourceCallBack = (newFileVO: WatermarkSourceUpdate) => {
    updatedFileVO.fileId = newFileVO.fileId;
    updatedFileVO.downloadAuthKey = newFileVO.downloadAuthKey;
    updatedFileVO.name = newFileVO.name;
    updatedFileVO.fileType = newFileVO.fileType;
    updatedFileVO.previewStatus = BatchStatus.COMPLETED;
    setUpdatedData(updatedFileVO);
    setDocLoaded(true);
  };

  // to get updated file...
  const payLoad = {
    [key]: [id],
    viewSourceCallBack,
    contentVO: isDocument ? sourceVO : fileVO
  };
  downloadProfileContent(payLoad as PopulateWatermarkDownloadPayload);
  return undefined;
};
